import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import moment from 'moment';
import ReactSlider from 'react-slider';
import {
  FiCalendar,
  FiUser,
  FiClock,
  FiDollarSign,
  FiTag,
  FiPlusCircle,
  FiTrash2,
} from 'react-icons/fi';
import './ConvertToTattooModal.css';

function ConvertToTattooModal({
  appointment,
  selectedLocationId,
  locations,
  onClose,
  onSuccess
}) {

  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [targetLocationId, setTargetLocationId] = useState(String(selectedLocationId));
  const [availableDays, setAvailableDays] = useState([]);

  const TATTOO_CATEGORIES = [
    'Realistic',
    'Fineline',
    'Lettering',
    'Anime',
    'Mandala',
    'Tribal',
    'Cover-Up',
    'Color',
    'Sonstige',
  ];

  const DURATION_OPTIONS = [30, 60, 120, 180, 240, 360];
  
  const DEPOSIT_OPTIONS = [50, 100, 150];
  const TIME_OPTIONS = [

    '10:00',
    '11:00',
    '12:00',
    '13:00',
    '14:00',
    '15:00',
    '16:00',
    '17:00',
    '18:00',
    '19:00'
  ];

  const createEmptySession = (overrides = {}) => ({
    tattooDate: '',
    artistId: '',
    time: appointment?.time?.substring(0, 5) || '',
    minRange: 900,
    maxRange: 1200,
    deposit: 150,
    duration: 360,
    tattoo_category: '',
    ...overrides,
  });

  const [sessions, setSessions] = useState([createEmptySession()]);

  useEffect(() => {
    if (!targetLocationId) return;

    axios
      .get(`/api/available_days_with_artists?locationId=${targetLocationId}`)
      .then(res =>
        setAvailableDays(Array.isArray(res.data) ? res.data : [])
      )
      .catch(err =>
        console.error("Tattootage konnten nicht geladen werden:", err)
      );
  }, [targetLocationId]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClose]);

  const availableTattooDates = useMemo(() => {
    const today = moment().startOf('day');

    return [
      ...new Set(
        availableDays
          .filter((day) => {
            const date = moment(day.day).startOf('day');
            const id = day.artistId || day.artist_id;
            const name = day.artistName || day.artist_name || day.name;
            return id && name && day.day && date.isSameOrAfter(today, 'day');
          })
          .map((day) => moment(day.day).format('YYYY-MM-DD'))
      ),
    ].sort();
  }, [availableDays]);

  const getArtistsForDate = (tattooDate) => {
    const uniqueArtists = new Map();

    availableDays
      .filter((day) => moment(day.day).format('YYYY-MM-DD') === tattooDate)
      .forEach((day) => {
        const id = day.artistId || day.artist_id;
        const name = day.artistName || day.artist_name || day.name;

        if (id && name) {
          uniqueArtists.set(String(id), { id, name });
        }
      });

    return Array.from(uniqueArtists.values());
  };

  const updateSession = (index, field, value) => {
    setSessions((prev) =>
      prev.map((session, i) =>
        i === index ? { ...session, [field]: value } : session
      )
    );
  };

  const PRICE_PRESETS = {
    30: { min: 150, max: 200 },
    60: { min: 200, max: 300 },
    120: { min: 400, max: 500 },
    180: { min: 600, max: 800 },
    240: { min: 800, max: 1000 },
    360: { min: 900, max: 1200 },
  };

  const addSession = () => {
    setSessions((prev) => {
      const last = prev[prev.length - 1] || {};

      return [
        ...prev,
        createEmptySession({
          tattooDate: '',
          artistId: last.artistId || '',
          time: '',
          minRange: last.minRange || '',
          maxRange: last.maxRange || '',
          deposit: '',
          duration: last.duration || 240,
          tattoo_category: last.tattoo_category || '',
        }),
      ];
    });
  };

  const removeSession = (index) => {
    if (sessions.length === 1) return;
    setSessions((prev) => prev.filter((_, i) => i !== index));
  };

  const goNext = () => {
    const first = sessions[0];

    if (!first.tattooDate || !first.artistId) {
      alert('Bitte Tattootag und Artist auswählen.');
      return;
    }

    setStep(2);
  };

  const validateSessions = () => {
    for (let i = 0; i < sessions.length; i++) {
      const session = sessions[i];

      if (!session.tattooDate || !session.artistId || !session.time) {
        alert(`Bitte Datum, Artist und Uhrzeit für Sitzung ${i + 1} auswählen.`);
        return false;
      }
    }

    return true;
  };

  const handleSubmit = async () => {
    if (!validateSessions()) return;

    try {
      setSaving(true);

      const payloadSessions = sessions.map((session, index) => ({
        sessionNumber: index + 1,
        tattooDate: session.tattooDate,
        artistId: Number(session.artistId),
        time: session.time,
        duration: Number(session.duration) || 240,
        minRange: Number(session.minRange) || 0,
        maxRange: Number(session.maxRange) || 0,
        deposit: Number(session.deposit) || 0,
        tattoo_category: session.tattoo_category || '',
      }));

      const res = await axios.put(
        `/api/appointments/${appointment.id}/convert-to-tattoo`,
        {
          targetLocationId: Number(targetLocationId),
          sessions: payloadSessions,
        }
      );

      onSuccess({
        ...res.data,
        targetLocationId: Number(targetLocationId),
      });
    } catch (err) {
      console.error('Umwandlung fehlgeschlagen:', err.response?.data || err);
      alert(err.response?.data?.message || 'Umwandlung fehlgeschlagen.');
    } finally {
      setSaving(false);
    }
  };

  const renderSessionFields = (session, index) => (
    <div key={index} className="pipeline-session-card">
      <div className="pipeline-session-card-header">
        <strong>Sitzung {index + 1}</strong>

        {sessions.length > 1 && (
          <button
            type="button"
            className="pipeline-session-remove"
            title="Sitzung entfernen"
            onClick={() => removeSession(index)}
            disabled={saving}
          >
            <FiTrash2 />
          </button>
        )}

        <div className="pipeline-appointment-edit-grid">
          <label>
            <span className="convert-field-title">
              <FiCalendar /> Tattootag
            </span>

            <select
              value={session.tattooDate}
              onChange={(e) =>
                setSessions((prev) =>
                  prev.map((item, i) =>
                    i === index
                      ? { ...item, tattooDate: e.target.value, artistId: '' }
                      : item
                  )
                )
              }
            >
              <option value="">Tattootag wählen</option>

              {availableTattooDates.map((date) => (
                <option key={date} value={date}>
                  {moment(date).format('dd, DD.MM.YYYY')}
                </option>
              ))}
            </select>
          </label>

          <label>
            <span className="convert-field-title">
              <FiUser /> Artist
            </span>

            <select
              value={session.artistId}
              disabled={!session.tattooDate}
              onChange={(e) => updateSession(index, 'artistId', e.target.value)}
            >
              <option value="">
                {session.tattooDate ? 'Artist wählen' : 'Erst Tattootag wählen'}
              </option>

              {getArtistsForDate(session.tattooDate).map((artist) => (
                <option key={artist.id} value={artist.id}>
                  {artist.name}
                </option>
              ))}
            </select>
          </label>
        </div>
      </div>

      <div className="convert-field">
        <span className="convert-field-title">
          <FiDollarSign /> Anzahlung
        </span>

        <div className="convert-chip-row">
          {DEPOSIT_OPTIONS.map((amount) => (
            <button
              key={amount}
              type="button"
              className={
                Number(session.deposit) === amount
                  ? 'convert-chip active'
                  : 'convert-chip'
              }
              onClick={() => updateSession(index, 'deposit', amount)}
            >
              {amount} €
            </button>
          ))}
        </div>

        <input
          type="number"
          value={session.deposit}
          onChange={(e) => updateSession(index, 'deposit', e.target.value)}
          placeholder="Eigener Betrag"
          className="convert-custom-input"
        />
      </div>

      <label className="convert-category-field">
        <span className="convert-field-title">
          <FiTag /> Kategorie
        </span>

        <select
          value={session.tattoo_category}
          onChange={(e) =>
            updateSession(index, 'tattoo_category', e.target.value)
          }
        >
          <option value="">Kategorie wählen</option>

          {TATTOO_CATEGORIES.map((category) => (
            <option key={category} value={category}>
              {category}
            </option>
          ))}
        </select>
      </label>

      <div className="pipeline-appointment-edit-grid">
        <div className="convert-field">
          <span className="convert-field-title">
            <FiClock /> Uhrzeit
          </span>

          <div className="convert-chip-row">
            {TIME_OPTIONS.map((time) => (
              <button
                key={time}
                type="button"
                className={
                  session.time === time
                    ? 'convert-chip active'
                    : 'convert-chip'
                }
                onClick={() => updateSession(index, 'time', time)}
              >
                {time}
              </button>
            ))}
          </div>

          {!TIME_OPTIONS.includes(session.time) && (
            <input
              type="time"
              value={session.time}
              onChange={(e) => updateSession(index, 'time', e.target.value)}
              className="convert-custom-input"
            />
          )}

          <button
            type="button"
            className="convert-other-time"
            onClick={() => {
              if (TIME_OPTIONS.includes(session.time)) {
                updateSession(index, 'time', '');
              }
            }}
          >
            Andere Uhrzeit
          </button>
        </div>
        <label>
          <span className="convert-field-title">
            <FiClock /> Dauer
          </span>

          <select
            value={session.duration}
            onChange={(e) => {
              const duration = Number(e.target.value);
              const preset = PRICE_PRESETS[duration];

              setSessions(prev =>
                prev.map((item, i) => {
                  if (i !== index) return item;

                  // Nur überschreiben, wenn der Benutzer den Preis noch nicht angepasst hat
                  const usingDefault =
                    !item.minRange ||
                    !item.maxRange ||
                    (
                      Number(item.minRange) === PRICE_PRESETS[item.duration]?.min &&
                      Number(item.maxRange) === PRICE_PRESETS[item.duration]?.max
                    );

                  return {
                    ...item,
                    duration,
                    minRange: usingDefault ? preset.min : item.minRange,
                    maxRange: usingDefault ? preset.max : item.maxRange,
                  };
                })
              );
            }}
          >
            {DURATION_OPTIONS.map((minutes) => (
              <option key={minutes} value={minutes}>
                {minutes} Minuten
              </option>
            ))}
          </select>
        </label>

      </div>

      <div className="convert-price-field">
        <span className="convert-field-title">
          <FiDollarSign /> Preisrahmen
        </span>

        <div className="convert-price-values">
          <strong>{session.minRange || 150} €</strong>
          <span>bis</span>
          <strong>{session.maxRange || 2400} €</strong>
        </div>

        <ReactSlider
          className="convert-price-slider"
          thumbClassName="convert-price-thumb"
          trackClassName="convert-price-track"
          min={150}
          max={2400}
          step={50}
          value={[
            Number(session.minRange) || 150,
            Number(session.maxRange) || 2400,
          ]}
          minDistance={50}
          onChange={([min, max]) => {
            updateSession(index, 'minRange', min);
            updateSession(index, 'maxRange', max);
          }}
          pearling
        />
      </div>

    </div>
  );

  return (
    <div className="pipeline-appointment-modal-overlay">
      <div
        className="pipeline-appointment-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <button className="pipeline-appointment-modal-close" onClick={onClose}>
          ×
        </button>

        <h3>In Stechtermin umwandeln</h3>
        <label className="pipeline-modal-full-label">
          Studio

          <select
            value={targetLocationId}
            onChange={(e) => {
              const locationId = e.target.value;

              setTargetLocationId(locationId);

              setSessions(prev =>
                prev.map(session => ({
                  ...session,
                  tattooDate: "",
                  artistId: "",
                }))
              );
            }}
          >
            {locations.map(location => (
              <option key={location.id} value={location.id}>
                {location.name}
              </option>
            ))}
          </select>
        </label>
        <p className="pipeline-appointment-modal-time">
          {appointment?.clientName || 'Ohne Name'}
        </p>

        <div className="pipeline-step-indicator">Schritt {step} von 2</div>

        {step === 1 && (
          <>
            {renderSessionFields(sessions[0], 0)}

            {availableTattooDates.length === 0 && (
              <p className="pipeline-empty">
                Keine zukünftigen Tattootage für diesen Standort.
              </p>
            )}

            <div className="pipeline-appointment-modal-actions">
              <button onClick={onClose} disabled={saving}>
                Abbrechen
              </button>

              <button
                className="primary"
                onClick={goNext}
                disabled={availableTattooDates.length === 0}
              >
                Weiter
              </button>
            </div>
          </>
        )}

        {step === 2 && (
          <>
            <div className="pipeline-sessions-list">
              {sessions.map((session, index) =>
                renderSessionFields(session, index)
              )}
            </div>

            <button
              type="button"
              className="pipeline-add-session-btn"
              onClick={addSession}
              disabled={saving}
            >
              <FiPlusCircle />
              Weitere Sitzung hinzufügen
            </button>

            <div className="pipeline-appointment-modal-actions">
              <button onClick={() => setStep(1)} disabled={saving}>
                Zurück
              </button>

              <button
                className="primary"
                onClick={handleSubmit}
                disabled={saving}
              >
                {saving
                  ? 'Wird umgewandelt...'
                  : sessions.length === 1
                    ? 'Stechtermin erstellen'
                    : `${sessions.length} Stechtermine erstellen`}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default ConvertToTattooModal;